import moment from 'moment';
import { head, get, find, last } from 'lodash';
import {
	dateFormat,
	PAYMENTS_LIMIT,
	TRANSACTION_LIMIT_ERROR_CODE,
	TYPE_IFT,
	FASTER_PAYMENTS_PENDING_STATUS_ERROR_CODE,
	TYPE_FASTER_PAYMENTS,
	TYPE_CHAPS,
	TYPE_BACS_CREDIT,
	TYPE_BACS_DEBIT,
	REPEATING_PAYMENT_LIMIT_ERROR_CODE,
} from '../../config';
import {
	getAccountIsBillerOrCreditCard,
	isSavingsAccount,
	isFutureDatedPaymentUnsupported,
	getPaymentsWithAccounts,
	getPaymentWithAccount,
} from '../accounts/selectors';

import {
	getBatchId,
	isBatch,
	getBatchAuthorisationStatus,
	getBatchName,
	isUnauthorisedDailyLimit as batchExceedsDailyLimit,
	getBatchAuthorisation,
	getBatchAuthorisers,
	getBatchContextId,
	getBatchGroupAuthorisers,
} from '../batch/selectors';
import {
	getShowChapsDailyLimitModal,
	getChapsDailyLimitModalDates,
	getChapsPaymentsList,
	getSingleChapsPaymentIdByToAccount,
} from '../chaps/selectors';
import {
	getRepeatingPaymentConfirmationRequest,
	getOneOffPaymentConfirmationRequest,
	getFutureDatedPaymentConfirmationRequest,
	getRepeatingPaymentReviewRequest,
	getOneOffPaymentReviewRequest,
	getBatchPaymentConfirmationRequest,
	getBatchPaymentReviewRequest,
	getFutureDatedBatchPaymentConfirmationRequest,
} from '../../helpers/apiRequestBuilder';

import { removeDuplicateFromaccounts } from '../../helpers/apiRequestBuilder/common';

import {
	isAuthorisationEdit,
	canAddPaymentWhenEditing,
	isMultiTransactions,
	isManagePaymentEdit,
	isChaps,
	isPaymentInEditMode,
	getPaymentType,
	hasGroupApprovalsEnabled,
} from '../modes/selectors';
import ApiConstants from '../../config/apiConstants';
import { getShowIFTDailyLimitModal, getIFTDailyLimitModalDates } from '../ift/selectors';
import {
	isBiller,
	unauthorisedDailyLimitFilter,
	toFormattedDate,
	toDistinctDates,
	fromPaymentToDateListItem,
} from '../commonSelectors/selectors';
import { getPaymentCategory } from './getPaymentCategory';
import { getPaymentsList } from './getPaymentsList';
import { getPrevious } from '../page/selectors';
import { pages } from '../page/actions';
import { getBacsCreditSinglePaymentIdByToAccount, getPayments as getBacsCreditPayments } from '../bacsCredit/selectors';
import { getBacsDebitSinglePaymentIdByToAccount, getPayments as getBacsDebitPayments } from '../bacsDebit/selectors';

export const isMultiplePayments = (state) => state.payments.length > 1;
export const isRepeatingPayment = (state) => {
	const firstPayment = head(state.payments);
	return firstPayment && firstPayment.type === 'repeating';
};

export const isExternalPayment = (state, paymentId) => {
	const payment = state.payments.find((payment) => payment.id === paymentId);
	const paymentWithAccount = getPaymentWithAccount(state, payment);
	return get(paymentWithAccount, 'toAccountDetails.type', '') === 'payee';
};

export const hasExternalPayments = (state) =>
	getPaymentsWithAccounts(state, getPaymentsList(state)).some(
		(payment) => get(payment, 'toAccountDetails.type', '') === 'payee'
	);

export const getPaymentIdsforSameReference = (state) =>
	getPaymentsList(state)
		.filter((payment) => !getAccountIsBillerOrCreditCard(state, payment.toAccount))
		.map((payment) => payment.id);

export const getPaymentIdsforSameDate = (state) =>
	getPaymentsList(state)
		.filter((payment) => !(isBiller(state, payment.toAccount) || isSavingsAccount(state, payment.fromAccount)))
		.map((payment) => payment.id);

export const getPaymentsTotal = (state) => {
	const payments = getPaymentsList(state);
	const total = payments.reduce((accumulator, payment) => {
		const amount = parseFloat(get(payment, 'amount', '').replace(/,/g, ''));
		return accumulator + (Number.isNaN(amount) ? 0 : amount);
	}, 0);
	return total.toLocaleString('en-GB', { minimumFractionDigits: 2 });
};

const paymentDoesNotHaveFromOrToAccount = (element) =>
	get(element, 'toAccount', '') === '' || get(element, 'fromAccount', '') === '';

const paymentHasToAccount = (payment) => get(payment, 'toAccount', '') !== '';

export const getPaymentsHaveFromAndToAccount = (state) =>
	!getPaymentsList(state).some(paymentDoesNotHaveFromOrToAccount);
export const hasPaymentWithToAccount = (state) => getPaymentsList(state).some(paymentHasToAccount);
export const isNumberOfPaymentsLimit = (state) => state.payments.length >= PAYMENTS_LIMIT;

const paymentIsObligate = (payment) => get(payment, 'authorisation.status', '') === ApiConstants.obligate;

export const getReviewedPaymentsList = (state) =>
	getPaymentsWithAccounts(state, getPaymentsList(state)).map((payment) => ({
		...payment,
		requiresAuth: paymentIsObligate(payment),
	}));
export const getFirstPaymentHasFromAndTo = (state) =>
	!!(get(head(state.payments), 'toAccount', false) && get(head(state.payments), 'fromAccount', false));

export const showAddNewPaymentButton = (state) => {
	if (isRepeatingPayment(state) || isNumberOfPaymentsLimit(state)) {
		return false;
	}
	if (isManagePaymentEdit(state)) {
		return false;
	}
	if (isAuthorisationEdit(state)) {
		return canAddPaymentWhenEditing(state);
	}

	if (isMultiTransactions(state) && !isMultiplePayments(state)) {
		return true;
	}

	if (!getPaymentsHaveFromAndToAccount(state)) {
		return false;
	}

	return true;
};

export const isPaymentsRequireAuthorisation = (state) => {
	if (isBatch(state)) return getBatchAuthorisationStatus(state) === ApiConstants.obligate;
	return getPaymentsList(state).some(paymentIsObligate);
};

const confirmedFilter = ({ confirmation: { responseCode = '' } = {} }) =>
	responseCode === 200 || responseCode === 422 || responseCode === 202;

export const getConfirmedPaymentsList = (state) =>
	getPaymentsWithAccounts(state, getPaymentsList(state).filter(confirmedFilter));

const awaitingAuthorisationFilter = ({ confirmation: { responseCode = '' } = {} }) => responseCode === 401;

export const getAwaitingAuthorisationPayments = (state) =>
	getPaymentsWithAccounts(state, getPaymentsList(state).filter(awaitingAuthorisationFilter));

const failedFilter = ({ confirmation: { responseCode = '', error: { code = '' } = {} } = {} }) =>
	[500, 409, 400, 404].indexOf(responseCode) > -1 && code !== FASTER_PAYMENTS_PENDING_STATUS_ERROR_CODE;

const pendingFilter = ({ confirmation: { responseCode = '', error: { code = '' } = {} } = {} }) =>
	responseCode === 409 && code === FASTER_PAYMENTS_PENDING_STATUS_ERROR_CODE;

export const getUnauthorisedDailyLimitDateList = (state) =>
	getPaymentsList(state)
		.filter(unauthorisedDailyLimitFilter)
		.map(toFormattedDate)
		.reduce(toDistinctDates, [])
		.map(fromPaymentToDateListItem);

export const getDistinctPaymentDateList = (state) =>
	getPaymentsList(state).map(toFormattedDate).reduce(toDistinctDates, []).map(fromPaymentToDateListItem);

export const getFailedPayments = (state) => getPaymentsWithAccounts(state, getPaymentsList(state).filter(failedFilter));

export const getPendingPayments = (state) =>
	getPaymentsWithAccounts(state, getPaymentsList(state).filter(pendingFilter));

export const isBatchEligible = (state) => {
	const payments = getPaymentsList(state);
	if (!(payments.length > 1)) return false;
	const firstPayment = head(payments);
	if (payments.some((payment) => !moment(payment.date).isSame(firstPayment.date, 'day'))) return false;
	if (payments.some((payment) => payment.fromAccount !== firstPayment.fromAccount)) return false;

	return true;
};

export const getPaymentsListToSubmitForReview = (state, beneficiaryId) => {
	const payments = getPaymentsWithAccounts(state, getPaymentsList(state));
	const { bankId } = state.session;
	const isContextIdRequired = isAuthorisationEdit(state);
	const isEditMode = isPaymentInEditMode(state);

	return {
		batch_list: payments.map(
			getOneOffPaymentReviewRequest(bankId, isContextIdRequired, isEditMode, null, beneficiaryId)
		),
	};
};

const fromPaymentsToCumulativeAmountPerDate = (accumulatedAmounts, payment) => {
	const formattedDate = moment(payment.date).format(dateFormat);
	const accumulatedAmount = get(accumulatedAmounts, formattedDate, 0);
	const paymentAmount = parseFloat(get(payment, 'amount', '').replace(/,/g, ''));

	return {
		...accumulatedAmounts,
		[formattedDate]: accumulatedAmount + (Number.isNaN(paymentAmount) ? 0 : paymentAmount),
	};
};

const findFirstPaymentsWithCumulativeAmounts = (payments, amounts) =>
	Object.keys(amounts).map((paymentDate) => {
		const firstPayment = find(payments, ({ date }) => moment(date).format(dateFormat) === paymentDate);
		const cumulativeAmount = amounts[paymentDate];

		return {
			...firstPayment,
			amount: cumulativeAmount.toLocaleString('en-GB', { minimumFractionDigits: 2 }),
		};
	});

export const getPaymentsListToSubmitForDailyLimitReview = (state, beneficiaryId) => {
	const paymentsList = getPaymentsList(state);
	const cumulativeAmounts = paymentsList.reduce(fromPaymentsToCumulativeAmountPerDate, {});
	const firstPaymentsWithCumulativeAmounts = findFirstPaymentsWithCumulativeAmounts(paymentsList, cumulativeAmounts);
	const payments = getPaymentsWithAccounts(state, firstPaymentsWithCumulativeAmounts);
	const { bankId } = state.session;
	const isContextIdRequired = isAuthorisationEdit(state);
	const isEditMode = isPaymentInEditMode(state);

	return {
		batch_list: payments.map(
			getOneOffPaymentReviewRequest(bankId, isContextIdRequired, isEditMode, null, beneficiaryId)
		),
	};
};

export const getRepeatingPaymentToSubmitForReview = (state) => {
	const payment = head(getPaymentsList(state));
	const paymentWithAccount = getPaymentWithAccount(state, payment);
	const { bankId } = state.session;
	const isContextIdRequired = isAuthorisationEdit(state);
	const isEditMode = isPaymentInEditMode(state);

	return getRepeatingPaymentReviewRequest(bankId, paymentWithAccount, isContextIdRequired, isEditMode);
};

const getConfirmedPaymentsListToSubmit = (state) => {
	const payments = getPaymentsWithAccounts(state, getPaymentsList(state));
	const { bankId } = state.session;
	const isEditMode = isPaymentInEditMode(state);

	return {
		batch_list: payments.map((payment) => {
			if (payment.date.isAfter()) return getFutureDatedPaymentConfirmationRequest(state, bankId, payment, isEditMode);
			return getOneOffPaymentConfirmationRequest(bankId, payment, isEditMode);
		}),
	};
};

const getConfirmedRepeatingPaymentToSubmit = (state) => {
	const payment = head(getPaymentsWithAccounts(state, getPaymentsList(state)));
	const { bankId } = state.session;
	const isEditMode = isPaymentInEditMode(state);

	return getRepeatingPaymentConfirmationRequest(state, bankId, payment, isEditMode);
};

// common for both chaps and faster payments
export const getBatchToSubmitForReview = (state, paymentsList) => {
	const payments = getPaymentsWithAccounts(state, paymentsList);
	const paymentCategory = getPaymentCategory(state);
	const { bankId } = state.session;
	const batchId = getBatchId(state);
	const batchContextId = getBatchContextId(state);
	const payment = head(payments);
	const total = parseFloat(getPaymentsTotal(state).replace(/,/g, ''));
	const isContextIdRequired = isAuthorisationEdit(state);
	const isEditMode = isPaymentInEditMode(state);

	const filteredPayments = removeDuplicateFromaccounts(payments);

	return {
		batch_list: [
			getBatchPaymentReviewRequest({
				bankId,
				batchId,
				total,
				payment_category: paymentCategory,
				payment,
				filteredPayments,
				isContextIdRequired,
				batchContextId,
				isEditMode,
			}),
		],
	};
};

const getConfirmedBatchToSubmit = (state) => {
	const payments = getPaymentsWithAccounts(state, getPaymentsList(state));
	const { bankId } = state.session;
	const authorisation = getBatchAuthorisation(state);
	const batchId = getBatchId(state);
	const batchName = getBatchName(state);
	const authorisers = getBatchAuthorisers(state);
	const groupAuthorisers = getBatchGroupAuthorisers(state);

	if (head(payments) && head(payments).date.isAfter()) {
		return {
			batch_list: payments.map(
				getFutureDatedBatchPaymentConfirmationRequest(
					bankId,
					batchId,
					batchName,
					authorisers,
					groupAuthorisers,
					state,
					authorisation
				)
			),
		};
	}

	return {
		batch_list: payments.map(
			getBatchPaymentConfirmationRequest(bankId, batchId, batchName, authorisers, groupAuthorisers, authorisation)
		),
	};
};

export const getConfirmedPaymentsToSubmit = (state) => {
	if (isBatch(state)) return getConfirmedBatchToSubmit(state);
	if (isRepeatingPayment(state)) return getConfirmedRepeatingPaymentToSubmit(state);
	return getConfirmedPaymentsListToSubmit(state);
};

export const getConfirmedBatchTotal = (state) => {
	const payments = getPaymentsList(state);

	const total = payments.reduce((accumulator, payment) => {
		const amount = parseFloat(get(payment, 'amount', '').replace(/,/g, ''));
		return accumulator + (Number.isNaN(amount) ? 0 : amount);
	}, 0);
	return total.toLocaleString('en-GB', { minimumFractionDigits: 2 });
};

export const getConfirmedBatchPayments = (state) => getPaymentsWithAccounts(state, getPaymentsList(state));

export const isFutureDatedPayment = (payment) => (moment.isMoment(payment.date) ? payment.date.isAfter() : false);

export const getFirstPaymentIsFutureDated = (state) => {
	const payment = head(state.payments);
	return payment && isFutureDatedPayment(payment);
};

export const getAnyPaymentToBillerOrCreditCard = (state) => {
	const payments = getPaymentsList(state);
	return payments.some((payment) => getAccountIsBillerOrCreditCard(state, payment.toAccount));
};

export const isAnyFutureDatedPaymentUnsupported = (state) => {
	const payments = getPaymentsList(state);
	return payments.some((payment) => isFutureDatedPaymentUnsupported(state, payment.fromAccount, payment.toAccount));
};

export const getFromAccount = (state) => {
	const payments = getPaymentsList(state);
	const result = get(head(payments), 'fromAccount', '');

	return result;
};

export const getFromAccounts = (state) => getPaymentsList(state).map((payment) => payment.fromAccount);

export const canApplySameForAll = (state) => {
	const payments = getPaymentsList(state);
	return !!(payments.length > 1 && get(payments, '1.toAccount'));
};

export const getFirstPayment = (state) => head(state.payments);

export const isUnauthorisedDailyLimit = (state) => getPaymentsList(state).some(unauthorisedDailyLimitFilter);

export const getShowDailyLimitModal = (state) => {
	if (get(state, 'modes.paymentType', '') === TYPE_IFT) {
		return getShowIFTDailyLimitModal(state);
	}
	if (isChaps(state)) {
		return getShowChapsDailyLimitModal(state) || batchExceedsDailyLimit(state);
	}
	return isUnauthorisedDailyLimit(state) || batchExceedsDailyLimit(state);
};
export const getDailyLimitModalDates = (state) => {
	if (get(state, 'modes.paymentType', '') === TYPE_IFT) {
		return getIFTDailyLimitModalDates(state);
	}
	if (isChaps(state)) {
		return getChapsDailyLimitModalDates(state);
	}
	if (isBatch(state)) {
		return getDistinctPaymentDateList(state);
	}
	return getUnauthorisedDailyLimitDateList(state);
};

const isTransactionLimitError = (authorisation) => get(authorisation, 'error.code', 0) === TRANSACTION_LIMIT_ERROR_CODE;
const isRepeatingPaymentLimitError = (authorisation) =>
	get(authorisation, 'error.code', 0) === REPEATING_PAYMENT_LIMIT_ERROR_CODE;
const isConflict = (authorisation) => get(authorisation, 'responseCode', 0) === 409;

export const hasTransactionLimitError = (authorisation) =>
	isConflict(authorisation) && (isTransactionLimitError(authorisation) || isRepeatingPaymentLimitError(authorisation));

export const getFPSingleAuthorisersList = (state, paymentId) =>
	get(
		getPaymentsList(state).find((payment) => payment.id === paymentId),
		'singleAuthoriserList',
		[]
	);

export const getFPMultiAuthorisersList = (state, paymentId) =>
	get(
		getPaymentsList(state).find((payment) => payment.id === paymentId),
		'multiAuthoriserList',
		[]
	);

export const getPaymentsRequireAuthorisers = (state) => {
	const payments = getPaymentsList(state);
	return payments.some((payment) => {
		const authorisersRequired = get(payment, 'authorisation.status', '') === ApiConstants.obligate;
		const hasAuthorisers = hasGroupApprovalsEnabled(state)
			? get(payment, 'authorisersGroup', []).length
			: get(payment, 'authorisers', []).length;
		return authorisersRequired && !hasAuthorisers;
	});
};

export const getLastPayment = (state) => last(getPaymentsList(state)) || {};

export const getSelectedPayment = (state) =>
	getPaymentsList(state).find(({ id }) => get(state, 'payeeSelector.selectedPaymentId', '') === id);

export const getPaymentCount = (state) => getPaymentsList(state).length;

export const getToAccount = (state) => {
	const selectedPaymentId = get(state, 'payeeSelector.selectedPaymentId', '');
	if (selectedPaymentId) {
		const payment = getPaymentsList(state).find(({ id }) => id === selectedPaymentId);
		return get(payment, 'toAccount', '');
	}
	return '';
};

export const getPaymentContextId = (state) => {
	const firstPayment = head(getPaymentsList(state));
	return get(firstPayment, 'context_id', get(firstPayment, 'id'));
};

export const getPaymentsFromPreviousPage = (state) => {
	const page = getPrevious(state);
	switch (page) {
		case pages.FasterPaymentsLandingPage:
			return getPaymentsList(state);
		case pages.ChapsLandingPage:
			return getChapsPaymentsList(state);
		case pages.BacsCreditLanding:
			return getBacsCreditPayments(state);
		case pages.BacsDebitLanding:
			return getBacsDebitPayments(state);
		default:
			return;
	}
};

export const getSinglePaymentIdByToAccount = (state, toAccount) => {
	const paymentsList = getPaymentsList(state);
	const payment = paymentsList.find((payment) => get(payment, 'toAccount') === toAccount);
	return get(payment, 'id', '');
};

export const getSinglePaymentIdByToAccountFromPaymentType = (state, toAccount) => {
	const paymentType = getPaymentType(state);
	switch (paymentType) {
		case TYPE_FASTER_PAYMENTS:
			return getSinglePaymentIdByToAccount(state, toAccount);
		case TYPE_CHAPS:
			return getSingleChapsPaymentIdByToAccount(state, toAccount);
		case TYPE_BACS_CREDIT:
			return getBacsCreditSinglePaymentIdByToAccount(state, toAccount);
		case TYPE_BACS_DEBIT:
			return getBacsDebitSinglePaymentIdByToAccount(state, toAccount);
		default:
			return;
	}
};

export const getIsSingularPaymentRefUpdated = (state) => {
	const paymentsList = getPaymentsList(state);

	if (paymentsList.length === 1) {
		const [{ initialReference, reference: currentReference }] = paymentsList;
		return initialReference !== currentReference;
	}

	return false;
};
